import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplace, CampaignType, Currency, StrategyConfigHistory } from "@front/m19-api-client";
import { CAMPAIGN_TYPE } from "@front/m19-injection-token";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  DAILY_BUDGET,
  IMPRESSIONS,
  Metric,
  MIN_DAILY_SPEND,
  MONTHLY_BUDGET,
  ROAS,
  TARGET_ACOS,
  TOTAL_SALES,
} from "@front/m19-metrics";
import { AdStatsEx, CurrencyStat } from "@front/m19-models";
import {
  AccountSelectionService,
  AdStatsWithTargetHistory,
  AuthService,
  CurrencyService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import {
  ChartConfig,
  IButtonDirective,
  IChartComponent,
  IIconComponent,
  ITabsComponent,
  ITooltipDirective,
  Option,
} from "@front/m19-ui";
import {
  DateAggregation,
  mergeAdStatsWithTargetHistory,
  mergeDspStatsWithTargetHistory,
  mergeSeveralDates,
  mergeSeveralDatesDsp,
} from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { DspStrategyStatsGridComponent } from "@m19-board/dsp/dsp-stats/dsp-strategy-stats-grid.component";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import { DSP_COST, DSP_SALES, getDspStrategyMetrics } from "@m19-board/models/DspMetrics";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { SbCreativeStatsGridComponent } from "@m19-board/strategies/sb-creative-stats-grid/sb-creative-stats-grid.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { StrategyPlacementStatsGridComponent } from "@m19-board/strategies/strategy-placement-stats/strategy-placement-stats-grid.component";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { map, shareReplay, switchMap } from "rxjs/operators";
import {
  ActivityEventAnnotationsService,
  ActivityEventType,
} from "../../../activities/activity-event-annotations.service";

@Component({
  selector: "app-strategy-page-stats",
  imports: [
    AsyncPipe,
    IButtonDirective,
    IIconComponent,
    ITooltipDirective,
    MetricSelectorComponent,
    ExportButtonComponent,
    SbCreativeStatsGridComponent,
    StrategyPlacementStatsGridComponent,
    TranslocoDirective,
    DspStrategyStatsGridComponent,
    ITabsComponent,
    ChartLayoutComponent,
    IChartComponent,
  ],
  templateUrl: "./strategy-page-stats.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageStatsComponent {
  private readonly strategyPageService = inject(StrategyPageService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly accountMarketplaceSelection = inject(AccountSelectionService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly statsService = inject(StatsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly currencyService = inject(CurrencyService);
  private readonly campaignType = inject(CAMPAIGN_TYPE);

  strategy = this.strategyPageService.strategy;

  @ViewChild("tacticPlacementGrid", { static: false }) tacticPlacementGrid!: StrategyPlacementStatsGridComponent;
  @ViewChild("creativeGrid", { static: false }) creativeGrid!: SbCreativeStatsGridComponent;
  @ViewChild("dspStrategyGrid", { static: false }) dspStatsGrid!: DspStrategyStatsGridComponent;

  private readonly metricsOnSameScale = [
    [TOTAL_SALES, AD_SALES, COST],
    [ACOS, TARGET_ACOS],
    [COST, MIN_DAILY_SPEND, DAILY_BUDGET, MONTHLY_BUDGET],
  ];
  private readonly dspMetricsOnSameScale = [
    [DSP_SALES, DSP_COST],
    [DSP_COST, MONTHLY_BUDGET],
  ];
  readonly CHART_METRICS: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];
  protected readonly CampaignType = CampaignType;
  private readonly localStorageKey: MetricsSelectorKey = {
    page: MetricsSelectorPage.strategy,
    campaignType: this.campaignType,
  };

  isDsp = computed(() => this.campaignType === CampaignType.DSP);
  readonly dspStrategyMetrics = computed(() =>
    this.isDsp() && this.strategy() ? getDspStrategyMetrics(this.strategy()!) : undefined,
  );
  readonly chartMetrics = computed<Metric<CurrencyStat>[]>(
    () => (this.dspStrategyMetrics()?.metrics as Metric<CurrencyStat>[]) ?? this.CHART_METRICS,
  );
  readonly metricsStorageKey = computed<MetricsSelectorKey>(() => ({
    ...this.localStorageKey,
    dspMetrics: this.dspStrategyMetrics()?.storageKeySuffix,
  }));
  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  dateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  displayEventAnnotation$ = new BehaviorSubject(false);
  dateAggregation = signal<DateAggregation>(DateAggregation.daily);
  private readonly dateAggregation$ = toObservable(this.dateAggregation);
  disableEventAnnotation = computed(() => this.dateAggregation() !== DateAggregation.daily);
  periodComparison = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });
  selectedMetrics = signal<Metric<AdStatsWithTargetHistory>[]>([]);
  readonly dspAdvertiserId = toSignal(
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(map((am) => am.dspAdvertiserId)),
  );
  isStatsChartHidden = !this.userSelectionService.getUserChartDisplayedPreference(this.localStorageKey);
  readonly SB_TABS = [
    {
      label: this.translocoService.translate("strategy-detail-stats-modal.by_creative"),
      value: 0,
    },
    {
      label: this.translocoService.translate("strategy-detail-stats-modal.by_tactic_placement"),
      value: 1,
    },
  ];
  readonly selectedStatsTab = signal(0); // for SB strategy stats
  readonly allEventAnnotationTypes = computed<Option<ActivityEventType>[]>(
    () => this.activityEventAnnotationsService.activityEventTypesOptionsPerCampaignType.get(this.campaignType!) ?? [],
  );

  private additionalMetrics = computed<Set<Metric<AdStatsWithTargetHistory>>>(() => {
    const additionalMetrics = new Set<Metric<AdStatsWithTargetHistory>>();
    for (const target of this.configHistory() ?? []) {
      if (target.dailyBudget !== undefined && !isNaN(target.dailyBudget)) additionalMetrics.add(DAILY_BUDGET);
      if (target.minDailySpend !== undefined && target.minDailySpend !== 0) additionalMetrics.add(MIN_DAILY_SPEND);
      if (target.monthlyBudget !== undefined && !isNaN(target.monthlyBudget)) additionalMetrics.add(MONTHLY_BUDGET);
    }
    return additionalMetrics;
  });

  private readonly dailyCampaignStats = toSignal<CurrencyStat[]>(
    combineLatest([
      this.accountMarketplaceSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
      toObservable(this.isDsp),
    ]).pipe(switchMap(([am, dateRange, currency, isDsp]) => this.getCampaignStats(am, dateRange, currency, isDsp))),
  );

  private readonly previousDailyCampaignStats = toSignal<CurrencyStat[]>(
    combineLatest([
      this.accountMarketplaceSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
      toObservable(this.isDsp),
    ]).pipe(
      switchMap(([am, pc, currency, isDsp]) =>
        pc?.period ? this.getCampaignStats(am, pc.period, currency, isDsp) : of([]),
      ),
    ),
  );

  private readonly annotations = toSignal(
    combineLatest([this.dateAggregation$, this.displayEventAnnotation$]).pipe(
      switchMap(([agg, b]) => {
        const strategy = this.strategy();
        if (agg == DateAggregation.daily && b && strategy) {
          return this.activityEventAnnotationsService.getStrategyActivityEventAnnotation(strategy);
        }
        return of([]);
      }),
    ),
  );

  configHistory = toSignal<StrategyConfigHistory[] | undefined>(
    combineLatest([
      this.accountMarketplaceSelection.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.selectedCurrency$,
      toObservable(this.strategy),
    ]).pipe(
      switchMap(([am, dr, currency, strategy]) =>
        this.statsService
          .getStrategyConfigHistory(am.accountId, am.marketplace, currency, am.dspAdvertiserId, dr[0], dr[1])
          .pipe(map((history) => history.filter((h) => h.strategyId === strategy?.strategyId))),
      ),
    ),
  );

  readonly chartData = computed<CurrencyStat[]>(() => {
    const configHistory = [];
    for (const target of this.configHistory() ?? []) {
      configHistory.push(target);
    }
    return [...configHistory, ...(this.dailyCampaignStats() ?? [])];
  });
  readonly chartConfig = computed<ChartConfig<CurrencyStat, "line">>(() => ({
    type: "line",
    aspectRatio: 4,
    aggFunctions: this.isDsp() ? [mergeDspStatsWithTargetHistory] : [mergeAdStatsWithTargetHistory],
    metrics: this.selectedMetrics(),
    dateAggregation: this.dateAggregation(),
    dateRange: this.dateRange(),
    previousPeriodData: this.previousDailyCampaignStats(),
    previousPeriod: this.periodComparison()?.period,
    metricsOnSameScale: this.isDsp() ? this.dspMetricsOnSameScale : this.metricsOnSameScale,
    eventAnnotations: this.annotations(),
  }));

  private globalDataMergeFn = computed(() => (this.isDsp() ? mergeSeveralDatesDsp : mergeSeveralDates));
  globalData = computed<CurrencyStat>(() => {
    const mergeFn = this.globalDataMergeFn();
    return (this.dailyCampaignStats() ?? []).reduce((globalData, d) => mergeFn(globalData, d), {});
  });
  previousPeriodGlobalData = computed<CurrencyStat>(() => {
    const mergeFn = this.globalDataMergeFn();
    return (this.previousDailyCampaignStats() ?? []).reduce((globalData, d) => mergeFn(globalData, d), {});
  });

  private getCampaignStats(
    am: AccountMarketplace,
    dateRange: string[],
    currency: Currency,
    isDsp: boolean,
  ): Observable<CurrencyStat[]> {
    return isDsp ? this.getDspCampaignStats(am, dateRange, currency) : this.getAdCampaignStats(am, dateRange, currency);
  }

  private getAdCampaignStats(
    am: AccountMarketplace,
    dateRange: string[],
    currency: Currency,
  ): Observable<CurrencyStat[]> {
    return this.statsService
      .getDailyCampaignStats(am.accountId, am.marketplace, currency, dateRange[0], dateRange[1])
      .pipe(
        map((data) => {
          const strategyData = data.filter((x) => x.strategyId == this.strategy()?.strategyId);
          return strategyData;
        }),
        shareReplay(1),
      );
  }

  private getDspCampaignStats(
    am: AccountMarketplace,
    dateRange: string[],
    currency: Currency,
  ): Observable<CurrencyStat[]> {
    return this.statsService
      .getDspLineItemsStats(am.accountId, am.marketplace, am.dspAdvertiserId!, currency, dateRange[0], dateRange[1])
      .pipe(
        map((data) => {
          return data.filter((x) => x.strategyId == this.strategy()?.strategyId);
        }),
        shareReplay(1),
      );
  }

  selectMetrics(metrics: Metric<AdStatsWithTargetHistory>[]) {
    if ([ACOS, COST].every((i) => metrics.includes(i))) {
      this.selectedMetrics.set([...metrics, TARGET_ACOS, ...this.additionalMetrics()]);
    } else if (metrics.some((m) => m === COST || m === DSP_COST)) {
      this.selectedMetrics.set([...metrics, ...this.additionalMetrics()]);
    } else if (metrics.some((m) => m === ACOS)) {
      this.selectedMetrics.set([...metrics, TARGET_ACOS]);
    } else {
      this.selectedMetrics.set([...metrics]);
    }
  }

  public selectAggregation(selection: DateAggregation): void {
    this.dateAggregation.set(selection);
  }

  toggleStatsChartDisplay(hide: boolean) {
    this.isStatsChartHidden = hide;
    this.userSelectionService.setUserChartDisplayedPreference(this.localStorageKey, !this.isStatsChartHidden);
  }

  exportStrategyStatsCsv() {
    if (this.strategy()?.campaignType == CampaignType.SB && this.selectedStatsTab() === 0) {
      this.creativeGrid.exportCsv();
    }
    if (this.strategy()?.campaignType == CampaignType.DSP) {
      this.dspStatsGrid.exportGridCsv();
    } else {
      this.tacticPlacementGrid.exportCsv();
    }
  }

  restoreDefaultColumns() {
    if (this.strategy()?.campaignType == CampaignType.SB && this.selectedStatsTab() === 0) {
      this.creativeGrid.restoreDefaultColumns();
    } else if (this.strategy()?.campaignType == CampaignType.DSP) {
      this.dspStatsGrid.restoreDefaultColumns();
    } else {
      this.tacticPlacementGrid.restoreDefaultColumns();
    }
  }
}
